import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Role } from '@lentera/shared';
import { ApiError, api, setUnauthorizedHandler } from '@/lib/api';

export interface AuthUser {
  id: string;
  name: string;
  email: string | null;
  username: string;
  role: Role;
  individualId?: string | null;
  tbmId?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login: (identifier: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

/**
 * Cookie penanda yang dipasang server saat login — bukan token, hanya "1".
 * Kalau penanda ada tapi tokennya sudah kedaluwarsa, /auth/me tetap menjawab
 * 401 dan alurnya kembali seperti semula. Penanda hanya menghemat satu
 * pertanyaan yang sudah pasti jawabannya, bukan menggantikan pemeriksaan.
 */
function hasSessionHint(): boolean {
  return document.cookie.split('; ').some((c) => c.startsWith('lentera_has_session='));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Sesi dipulihkan dari cookie HttpOnly — tidak ada token di localStorage,
  // sehingga tidak terbaca skrip pihak ketiga.
  useEffect(() => {
    let cancelled = false;

    // Tanpa penanda ini, tamu yang belum login tetap akan bertanya ke server
    // dan selalu dijawab 401 — galat merah di console yang sebenarnya adalah
    // jawaban yang benar, tapi terlihat seperti kerusakan. Penanda hanya
    // menentukan PERLU BERTANYA ATAU TIDAK; sahnya sesi tetap diputuskan
    // server dari token HttpOnly, dan 401 di bawah tetap ditangani.
    if (!hasSessionHint()) {
      setLoading(false);
      return;
    }

    api
      .get<AuthUser>('/auth/me')
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.isUnauthorized) {
          // Penanda tertinggal padahal tokennya sudah kedaluwarsa — dibuang,
          // supaya muat-ulang berikutnya tidak bertanya lagi dan 401-nya tidak
          // muncul berkali-kali.
          document.cookie = 'lentera_has_session=; Max-Age=0; path=/';
        } else {
          console.error('Gagal memulihkan sesi', err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Sesi yang mati di tengah pemakaian langsung mengosongkan status login,
   * sehingga RequireAuth memindahkan pengguna ke halaman masuk. Sebelumnya
   * aplikasi tetap tampak login sementara setiap penyimpanan gagal — pengguna
   * membaca "silakan masuk kembali" tanpa diberi jalan ke sana.
   */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      document.cookie = 'lentera_has_session=; Max-Age=0; path=/';
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (identifier: string, password: string, remember = false) => {
    const me = await api.post<AuthUser>('/auth/login', { identifier, password, remember });
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth harus dipakai di dalam <AuthProvider>');
  return ctx;
}
